import { logError } from '@modernloop/shared/utils';

import { useEmployeePrefStringArray } from 'src/hooks/api/employee/get';
import { useSetEmployeePrefStringArray } from 'src/hooks/api/employee/set';
import useEmployeeId from 'src/hooks/useEmployeeId';

import { EmployeePrefName } from 'src/utils/api/employee';

import DoNotShowAgainKey from './DoNotShowAgainKey';

type DoNotShowAgainHookResult = {
  /**
   * if we are still loading the do not show again preference
   */
  isDoNotShowAgainLoading: boolean;

  /**
   * Whether we should show this content or not
   */
  doNotShowAgain: boolean;

  /**
   * Add the key to the do not show again preference
   */
  addToDoNotShowAgain: () => Promise<void>;
};

/**
 * Utility hook to manage the "do not show again" behavior
 */
const useDoNotShowAgain = (key: DoNotShowAgainKey | null | undefined): DoNotShowAgainHookResult => {
  const currentEmployeeId = useEmployeeId();

  const [data, { loading, refetch }] = useEmployeePrefStringArray(
    EmployeePrefName.ARR_DO_NOT_SHOW_AGAIN_MODALS,
    currentEmployeeId || ''
  );

  const doNotShowAgainKeys = (data || []) as DoNotShowAgainKey[];

  const updatePref = useSetEmployeePrefStringArray(
    EmployeePrefName.ARR_DO_NOT_SHOW_AGAIN_MODALS,
    currentEmployeeId || ''
  );

  if (!key || loading) {
    return {
      isDoNotShowAgainLoading: loading,
      doNotShowAgain: false,
      addToDoNotShowAgain: async () => {},
    };
  }

  const addToDoNotShowAgain = async () => {
    if (!key || doNotShowAgainKeys.includes(key) || loading) {
      return;
    }
    try {
      const newList = [...doNotShowAgainKeys, key];
      await updatePref(newList);
      await refetch();
    } catch (error) {
      logError(`Failed to add '${key}' to do not show again preferences`, error);
    }
  };

  return {
    isDoNotShowAgainLoading: loading,
    doNotShowAgain: key && doNotShowAgainKeys.includes(key),
    addToDoNotShowAgain,
  };
};

export default useDoNotShowAgain;
